import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { Typography } from '../styles/primitives';
import { fetchOrderBook, OrderBookEntry } from '../services/polymarketApi';

interface EventOrderBookProps {
  tokenId: string;
}

interface ProcessedEntry {
  price: number;
  shares: number;
  total: number;
  cumTotal: number;
}

function processEntries(
  entries: OrderBookEntry[],
  direction: 'asc' | 'desc',
  limit: number
): ProcessedEntry[] {
  const sorted = entries
    .map(e => ({
      price: parseFloat(e.price),
      shares: parseFloat(e.size),
      total: parseFloat(e.price) * parseFloat(e.size),
      cumTotal: 0,
    }))
    .filter(e => !isNaN(e.price) && !isNaN(e.shares) && e.shares > 0)
    .sort((a, b) => direction === 'desc' ? b.price - a.price : a.price - b.price)
    .slice(0, limit);

  let cum = 0;
  for (const entry of sorted) {
    cum += entry.total;
    entry.cumTotal = cum;
  }
  return sorted;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const EventOrderBook: React.FC<EventOrderBookProps> = ({ tokenId }) => {
  const [bids, setBids] = useState<ProcessedEntry[]>([]);
  const [asks, setAsks] = useState<ProcessedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [side, setSide] = useState<'yes' | 'no'>('yes');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const loadBook = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    const data = await fetchOrderBook(tokenId);
    const processedAsks = processEntries(data.asks, 'asc', 8);
    const processedBids = processEntries(data.bids, 'desc', 8);
    setAsks(processedAsks.reverse());
    setBids(processedBids);
    setLastUpdated(new Date());
    setLoading(false);
    setRefreshing(false);
  }, [tokenId]);

  useEffect(() => {
    setLoading(true);
    loadBook();
    intervalRef.current = setInterval(() => loadBook(), 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tokenId, loadBook]);

  const maxCumTotal = Math.max(
    ...bids.map(b => b.cumTotal),
    ...asks.map(a => a.cumTotal),
    1
  );

  const bestAsk = asks.length > 0 ? asks[asks.length - 1].price : 0;
  const bestBid = bids.length > 0 ? bids[0].price : 0;
  const spread = bestAsk > 0 && bestBid > 0 ? bestAsk - bestBid : 0;
  const lastPrice = bestBid > 0 ? bestBid : bestAsk;

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-2">
          <span
            style={{
              fontSize: Typography.BodySmall,
              fontWeight: Typography.WeightSemibold,
              color: '#0f172a',
            }}
          >
            Order Book
          </span>
          {!loading && (bids.length > 0 || asks.length > 0) && (
            <div className="flex items-center gap-1.5">
              <div
                className="animate-pulse"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#10b981',
                }}
              />
              <span style={{ fontSize: Typography.Fine, color: '#94a3b8' }}>
                Live
              </span>
            </div>
          )}
        </div>
        {expanded ? (
          <ChevronUp size={16} style={{ color: '#94a3b8' }} />
        ) : (
          <ChevronDown size={16} style={{ color: '#94a3b8' }} />
        )}
      </button>

      {expanded && (
        <div>
          <div
            className="px-4 pb-3 flex items-center justify-between"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
          >
            <div className="flex gap-4">
              <button
                onClick={() => setSide('yes')}
                style={{
                  fontSize: Typography.Caption,
                  fontWeight: Typography.WeightSemibold,
                  color: side === 'yes' ? '#0f172a' : '#94a3b8',
                  borderBottom:
                    side === 'yes' ? '2px solid #0f172a' : '2px solid transparent',
                  paddingBottom: '8px',
                }}
              >
                Trade Yes
              </button>
              <button
                onClick={() => setSide('no')}
                style={{
                  fontSize: Typography.Caption,
                  fontWeight: Typography.WeightSemibold,
                  color: side === 'no' ? '#0f172a' : '#94a3b8',
                  borderBottom:
                    side === 'no' ? '2px solid #0f172a' : '2px solid transparent',
                  paddingBottom: '8px',
                }}
              >
                Trade No
              </button>
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span style={{ fontSize: Typography.Fine, color: '#c4c4c4' }}>
                  {formatTime(lastUpdated)}
                </span>
              )}
              <button
                onClick={() => loadBook(true)}
                className="p-1.5 rounded-md transition-colors"
                style={{ color: '#94a3b8' }}
                title="Refresh"
              >
                <RefreshCw
                  size={14}
                  className={refreshing ? 'animate-spin' : ''}
                />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={20} className="animate-spin" style={{ color: '#94a3b8' }} />
            </div>
          ) : asks.length === 0 && bids.length === 0 ? (
            <div className="text-center py-10">
              <span style={{ fontSize: Typography.Caption, color: '#94a3b8' }}>
                No order book data available
              </span>
            </div>
          ) : (
            <div>
              <div
                className="grid px-4 py-2"
                style={{
                  gridTemplateColumns: '28px 1fr 72px 80px 86px',
                  gap: '0 6px',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <span />
                <span
                  style={{
                    fontSize: Typography.Fine,
                    color: '#94a3b8',
                    fontWeight: Typography.WeightSemibold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {side === 'yes' ? 'TRADE YES' : 'TRADE NO'}
                </span>
                <span
                  className="text-right"
                  style={{
                    fontSize: Typography.Fine,
                    color: '#94a3b8',
                    fontWeight: Typography.WeightSemibold,
                  }}
                >
                  PRICE
                </span>
                <span
                  className="text-right"
                  style={{
                    fontSize: Typography.Fine,
                    color: '#94a3b8',
                    fontWeight: Typography.WeightSemibold,
                  }}
                >
                  SHARES
                </span>
                <span
                  className="text-right"
                  style={{
                    fontSize: Typography.Fine,
                    color: '#94a3b8',
                    fontWeight: Typography.WeightSemibold,
                  }}
                >
                  TOTAL
                </span>
              </div>

              {asks.map((ask, i) => (
                <div
                  key={`ask-${i}`}
                  className="grid px-4 py-1.5 relative"
                  style={{
                    gridTemplateColumns: '28px 1fr 72px 80px 86px',
                    gap: '0 6px',
                  }}
                >
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{
                      width: `${(ask.cumTotal / maxCumTotal) * 100}%`,
                      background: 'rgba(239, 68, 68, 0.08)',
                      transition: 'width 300ms ease',
                    }}
                  />
                  <span className="relative z-10 flex items-center">
                    {i === 0 && (
                      <span
                        className="inline-block px-1.5 py-0.5 text-white rounded"
                        style={{
                          fontSize: '9px',
                          background: '#ef4444',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Asks
                      </span>
                    )}
                  </span>
                  <span className="relative z-10" />
                  <span
                    className="text-right font-mono relative z-10"
                    style={{
                      fontSize: Typography.Caption,
                      color: '#ef4444',
                      fontWeight: Typography.WeightSemibold,
                    }}
                  >
                    {(ask.price * 100).toFixed(0)}c
                  </span>
                  <span
                    className="text-right font-mono relative z-10"
                    style={{ fontSize: Typography.Caption, color: '#475569' }}
                  >
                    {ask.shares.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span
                    className="text-right font-mono relative z-10"
                    style={{ fontSize: Typography.Caption, color: '#475569' }}
                  >
                    ${ask.cumTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}

              <div
                className="flex items-center justify-between px-4 py-2.5"
                style={{
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(0,0,0,0.02)',
                }}
              >
                <span
                  className="font-mono"
                  style={{
                    fontSize: Typography.Caption,
                    color: '#475569',
                    fontWeight: Typography.WeightSemibold,
                  }}
                >
                  Last: {(lastPrice * 100).toFixed(0)}c
                </span>
                <span
                  className="font-mono"
                  style={{ fontSize: Typography.Caption, color: '#94a3b8' }}
                >
                  Spread: {(spread * 100).toFixed(0)}c
                </span>
              </div>

              {bids.map((bid, i) => (
                <div
                  key={`bid-${i}`}
                  className="grid px-4 py-1.5 relative"
                  style={{
                    gridTemplateColumns: '28px 1fr 72px 80px 86px',
                    gap: '0 6px',
                  }}
                >
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{
                      width: `${(bid.cumTotal / maxCumTotal) * 100}%`,
                      background: 'rgba(16, 185, 129, 0.08)',
                      transition: 'width 300ms ease',
                    }}
                  />
                  <span className="relative z-10 flex items-center">
                    {i === 0 && (
                      <span
                        className="inline-block px-1.5 py-0.5 text-white rounded"
                        style={{
                          fontSize: '9px',
                          background: '#10b981',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Bids
                      </span>
                    )}
                  </span>
                  <span className="relative z-10" />
                  <span
                    className="text-right font-mono relative z-10"
                    style={{
                      fontSize: Typography.Caption,
                      color: '#10b981',
                      fontWeight: Typography.WeightSemibold,
                    }}
                  >
                    {(bid.price * 100).toFixed(0)}c
                  </span>
                  <span
                    className="text-right font-mono relative z-10"
                    style={{ fontSize: Typography.Caption, color: '#475569' }}
                  >
                    {bid.shares.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span
                    className="text-right font-mono relative z-10"
                    style={{ fontSize: Typography.Caption, color: '#475569' }}
                  >
                    ${bid.cumTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}

              <div
                className="px-4 py-2.5 flex items-center justify-end"
                style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}
              >
                <span style={{ fontSize: Typography.Fine, color: '#c4c4c4' }}>
                  Live Order Book
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventOrderBook;
